import React, { useMemo, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SendIcon from '@material-ui/icons/Send';
import { makeStyles } from '@material-ui/core/styles';

import {
  BODY_MAX_LENGTH,
  CONSENT_BUTTON_TEXTS,
  FOOTER_MAX_LENGTH,
  placeholderNumbers,
  ratioInfo,
  renderTemplate,
  TEMPLATE_CATEGORIES,
  TEMPLATE_LANGUAGES,
  TEMPLATE_NAME_PATTERN,
} from '../../../constants/whatsapp';
import { colors } from '../theme';
import FieldLabel from './FieldLabel';
import TemplateImageEditor from './TemplateImageEditor';
import WhatsappPreview from './WhatsappPreview';
import { KYIV_ZONE } from '../kyivTime';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2.5),
    borderRadius: 10,
  },
  cardTitle: {
    color: colors.text,
    fontWeight: 700,
    fontSize: '16px',
    marginBottom: theme.spacing(2.5),
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  insert: {
    marginTop: theme.spacing(1),
  },
  rules: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2.5),
    fontSize: '12px',
    color: colors.textDim,
    '& li': { marginBottom: theme.spacing(0.5) },
  },
  counter: {
    display: 'block',
    textAlign: 'right',
    marginTop: theme.spacing(0.5),
  },
  param: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: colors.panelAlt,
  },
  paramTitle: {
    fontWeight: 700,
    fontSize: '13px',
    color: colors.accent,
    marginBottom: theme.spacing(1.5),
  },
  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.75),
    fontSize: '13px',
  },
  checkIcon: {
    fontSize: '18px',
    marginRight: theme.spacing(1),
    marginTop: '1px',
  },
  sticky: {
    position: 'sticky',
    top: theme.spacing(11),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  submit: {
    marginRight: theme.spacing(1.5),
    backgroundColor: colors.accent,
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': { backgroundColor: colors.accentHover },
  },
  submitError: {
    color: '#d32f2f',
    marginTop: theme.spacing(1.5),
  },
}));

const currentTime = () =>
  new Date().toLocaleTimeString('uk-UA', { timeZone: KYIV_ZONE, hour: '2-digit', minute: '2-digit' });

// Meta не считает знаки препинания: «…{{4}}.» для неё всё ещё заканчивается переменной.
const edgeText = (text) =>
  text.trim().replace(/^[^\p{L}\p{N}{]+/u, '').replace(/[^\p{L}\p{N}}]+$/u, '');

const emptyParam = { label: '', example: '', auto: false, multiline: false };
const emptyParams = (params) =>
  Object.keys(params).reduce(
    (acc, key) => ({ ...acc, [key]: { ...emptyParam, ...params[key] } }),
    {}
  );

// Подписи и примеры переменных сохранённого шаблона в том виде, в каком их держит форма.
const paramsFromTemplate = (params) =>
  (params || []).reduce(
    (acc, p) => ({
      ...acc,
      [String(p.key)]: {
        ...emptyParam,
        label: p.label || '',
        example: p.example || p.placeholder || '',
        auto: p.auto === 'contactName' || p.auto === true,
        multiline: Boolean(p.multiline),
      },
    }),
    {}
  );

// Форма шаблона: текст с переменными, пример для каждой, превью как в WhatsApp и
// проверка тех же правил, что делает Meta при модерации. С `initial` (шаблон с
// сервера) форма редактирует его: название в Meta, язык и категорию менять нельзя.
function TemplateForm(props) {
  const classes = useStyles();
  const { onSubmit, onCancel, initial } = props;
  const editing = Boolean(initial);
  const bodyRef = useRef(null);

  const [title, setTitle] = useState(editing ? initial.title : '');
  const [description, setDescription] = useState(editing ? initial.description : '');
  const [name, setName] = useState(editing ? initial.templateName : '');
  const [category, setCategory] = useState(editing ? initial.category : 'MARKETING');
  const [language, setLanguage] = useState(editing ? initial.language : 'uk');
  const [body, setBody] = useState(editing ? initial.body : '');
  const [footer, setFooter] = useState(editing ? initial.footer || '' : '');
  const [paramMeta, setParamMeta] = useState(editing ? paramsFromTemplate(initial.params) : {});
  const [useImage, setUseImage] = useState(Boolean(editing && initial.image));
  const [consentButtons, setConsentButtons] = useState(Boolean(editing && initial.consentButtons));
  const [image, setImage] = useState(
    editing && initial.image
      ? { file: initial.image.file, fields: initial.image.config.fields }
      : { file: '', fields: [] }
  );
  const [previewImage, setPreviewImage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const numbers = useMemo(() => placeholderNumbers(body), [body]);

  const getParam = (key) => paramMeta[key] || emptyParam;
  const setParam = (key, patch) => {
    setParamMeta({ ...paramMeta, [key]: { ...getParam(key), ...patch } });
    setSubmitError('');
  };

  // Готовая афиша приходит вместе с текстом шаблона и переменными. Пустые поля
  // заполняем, а то, что менеджер уже написал сам, не трогаем.
  const handleStarter = (starter) => {
    if (!starter) return;
    if (!title.trim()) setTitle(starter.title);
    if (!description.trim()) setDescription(starter.description);
    if (!name) setName(starter.name);
    if (!body.trim()) {
      setCategory(starter.category);
      setLanguage(starter.language);
      setBody(starter.body);
      setParamMeta(emptyParams(starter.params));
    }
    setSubmitError('');
  };

  const handleInsertVariable = () => {
    const next = numbers.length ? Math.max(...numbers) + 1 : 1;
    const token = `{{${next}}}`;
    const el = bodyRef.current;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    setSubmitError('');
    if (el) {
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      }, 0);
    }
  };

  const exampleValues = numbers.map((n) => getParam(String(n)).example.trim());
  const exampleByKey = {};
  numbers.forEach((n) => {
    exampleByKey[String(n)] = getParam(String(n)).example.trim();
  });

  const usedOnImage = new Set();
  image.fields.forEach((field) => {
    field.text.replace(/\{\{(\d+)\}\}/g, (match, key) => {
      usedOnImage.add(Number(key));
      return match;
    });
  });
  const unknownOnImage = Array.from(usedOnImage).filter((key) => !numbers.includes(key));
  const previewText = body.trim()
    ? renderTemplate({ body }, exampleValues)
    : '';

  const ratio = ratioInfo(body);
  const trimmed = body.trim();
  const edgeCheck = edgeText(body);
  const sequential = numbers.every((n, index) => n === index + 1);
  const leftoverBraces = /\{\{|\}\}/.test(body.replace(/\{\{\d+\}\}/g, ''));

  const checks = [
    {
      ok: TEMPLATE_NAME_PATTERN.test(name),
      text: 'Название в Meta: строчные латинские буквы, цифры, подчёркивание',
    },
    {
      ok: trimmed.length > 0 && body.length <= BODY_MAX_LENGTH,
      text: `Текст не пустой и не длиннее ${BODY_MAX_LENGTH} символов (${body.length})`,
    },
    {
      ok: sequential && !leftoverBraces,
      text: 'Переменные вида {{1}}, {{2}} по порядку, без названий и повторов',
    },
    {
      ok:
        trimmed.length > 0 &&
        !/^\{\{\d+\}\}/.test(edgeCheck) &&
        !/\{\{\d+\}\}$/.test(edgeCheck),
      text: 'Шаблон не начинается и не заканчивается переменной',
    },
    {
      ok: trimmed.length > 0 && ratio.total >= ratio.required,
      text: `Достаточно текста вокруг переменных: слов и переменных ${ratio.total}, нужно не меньше ${ratio.required}`,
    },
    {
      ok: footer.length <= FOOTER_MAX_LENGTH && !/\{\{/.test(footer),
      text: `Колонтитул до ${FOOTER_MAX_LENGTH} символов и без переменных (${footer.length})`,
    },
    {
      ok: numbers.every((n) => {
        const p = getParam(String(n));
        return p.label.trim() && p.example.trim();
      }),
      text: 'У каждой переменной есть подпись и пример значения',
    },
  ];

  if (useImage) {
    checks.push({
      ok: Boolean(image.file) && image.fields.length > 0 && unknownOnImage.length === 0,
      text:
        unknownOnImage.length > 0
          ? `Картинка: переменной {{${unknownOnImage[0]}}} нет в тексте шаблона`
          : 'Картинка: выбран фон и есть хотя бы один текст',
    });
  }

  const allOk = checks.every((item) => item.ok) && title.trim().length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    const result = await onSubmit({
      name,
      language,
      category,
      body,
      footer: footer.trim(),
      title: title.trim(),
      description: description.trim(),
      ...(consentButtons && { consentButtons: true }),
      ...(useImage && { image: { file: image.file, config: { fields: image.fields } } }),
      params: numbers.map((n) => {
        const p = getParam(String(n));
        return {
          key: String(n),
          label: p.label.trim(),
          example: p.example.trim(),
          auto: p.auto,
          multiline: p.multiline,
        };
      }),
    });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Paper className={classes.card}>
          <Typography className={classes.cardTitle}>
            {editing ? 'Редактирование шаблона' : 'Новый шаблон'}
          </Typography>

          <FieldLabel
            label="Название для менеджеров"
            hint="Так шаблон будет называться в списке рассылки"
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Обновление цен"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </FieldLabel>

          <FieldLabel
            label="Название в Meta"
            hint={
              editing
                ? 'Название в Meta изменить нельзя'
                : 'Строчные латинские буквы, цифры и _. После отправки изменить нельзя'
            }
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="price_update"
              disabled={editing}
              value={name}
              error={name.length > 0 && !TEMPLATE_NAME_PATTERN.test(name)}
              onChange={(event) => setName(event.target.value)}
            />
          </FieldLabel>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FieldLabel
                label="Категория"
                hint={
                  editing
                    ? 'Категорию Meta менять не даёт'
                    : (TEMPLATE_CATEGORIES.find((item) => item.value === category) || {}).hint
                }
              >
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  disabled={editing}
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  {TEMPLATE_CATEGORIES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FieldLabel>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel label="Язык">
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  disabled={editing}
                  value={language}
                  onChange={(event) => setLanguage(event.target.value)}
                >
                  {TEMPLATE_LANGUAGES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FieldLabel>
            </Grid>
          </Grid>

          <FieldLabel
            label="Текст шаблона"
            hint="Переменная — это «дырка», куда при отправке подставляется своё значение"
          >
            <TextField
              fullWidth
              multiline
              rows={7}
              variant="outlined"
              inputProps={{ style: { resize: 'vertical' } }}
              placeholder="Доброго дня, {{1}}! Оновлення цін GradOil станом на {{2}}."
              value={body}
              inputRef={bodyRef}
              onChange={(event) => {
                setBody(event.target.value);
                setSubmitError('');
              }}
            />
            <div className={classes.row}>
              <Button
                size="small"
                className={classes.insert}
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleInsertVariable}
              >
                Вставить переменную {`{{${numbers.length ? Math.max(...numbers) + 1 : 1}}}`}
              </Button>
            </div>
            <Typography
              variant="caption"
              color={body.length > BODY_MAX_LENGTH ? 'error' : 'textSecondary'}
              className={classes.counter}
            >
              {body.length} / {BODY_MAX_LENGTH}
            </Typography>
            <ul className={classes.rules}>
              <li>
                Шаблон не может начинаться или заканчиваться переменной.
                Знаки препинания не считаются: «…{'{{4}}'}.» тоже ошибка, после
                последней переменной нужно хотя бы одно слово.
              </li>
              <li>
                Переменные только вида {'{{1}}'}, {'{{2}}'}, по порядку, без
                названий и повторов.
              </li>
              <li>
                Текста вокруг переменных должно быть достаточно: слов и
                переменных вместе не меньше, чем 3 × (число переменных) + 1.
              </li>
              <li>
                Не больше {BODY_MAX_LENGTH} символов вместе с переменными.
              </li>
              <li>
                Не ставьте две переменные подряд без слов между ними.
              </li>
            </ul>
          </FieldLabel>

          <FieldLabel
            label="Колонтитул (необязательно)"
            hint="Мелкий серый текст под сообщением, без переменных"
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Щоб відмовитися від розсилки, дайте відповідь СТОП"
              value={footer}
              error={footer.length > FOOTER_MAX_LENGTH}
              onChange={(event) => setFooter(event.target.value)}
            />
          </FieldLabel>

          <FieldLabel label="Описание для менеджеров (необязательно)">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Актуальные цены на масло для клиентов"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FieldLabel>
        </Paper>

        {numbers.length > 0 && (
          <Paper className={classes.card}>
            <Typography className={classes.cardTitle}>Переменные</Typography>
            {numbers.map((n) => {
              const key = String(n);
              const p = getParam(key);
              return (
                <div key={key} className={classes.param}>
                  <Typography className={classes.paramTitle}>{`{{${n}}}`}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label="Подпись поля"
                        placeholder="Дата цен"
                        value={p.label}
                        onChange={(event) =>
                          setParam(key, { label: event.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label="Пример значения"
                        placeholder="11.10.2026"
                        value={p.example}
                        onChange={(event) =>
                          setParam(key, { example: event.target.value })
                        }
                      />
                    </Grid>
                  </Grid>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={p.auto}
                        onChange={(event) =>
                          setParam(key, { auto: event.target.checked })
                        }
                      />
                    }
                    label="Имя клиента: подставлять из карточки контакта"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={p.multiline}
                        onChange={(event) =>
                          setParam(key, { multiline: event.target.checked })
                        }
                      />
                    }
                    label="Длинный текст (большое поле ввода)"
                  />
                </div>
              );
            })}
          </Paper>
        )}

        <Paper className={classes.card}>
          <Typography className={classes.cardTitle}>Запрос согласия клиента</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={consentButtons}
                onChange={(event) => {
                  setConsentButtons(event.target.checked);
                  setSubmitError('');
                }}
              />
            }
            label="Добавить кнопки «Так / Ні» под сообщением"
          />
          <Typography variant="caption" color="textSecondary" display="block">
            Для приветственной рассылки. Клиент нажимает кнопку, и ответ сам
            записывается в карточку контакта как согласие или отказ. Тексты кнопок
            зависят от языка шаблона. Написав СТОП, клиент отказывается в любом случае.
          </Typography>
        </Paper>

        <Paper className={classes.card}>
          <Typography className={classes.cardTitle}>Картинка в начале сообщения</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={useImage}
                onChange={(event) => {
                  setUseImage(event.target.checked);
                  setSubmitError('');
                }}
              />
            }
            label="Добавить картинку (текст на ней можно менять при каждой рассылке)"
          />
          {useImage && (
            <TemplateImageEditor
              image={image}
              onChange={setImage}
              variables={numbers.map((n) => ({
                key: String(n),
                label: getParam(String(n)).label,
              }))}
              values={exampleByKey}
              onPreview={setPreviewImage}
              onStarter={handleStarter}
            />
          )}
        </Paper>

        <Paper className={classes.card}>
          <Typography className={classes.cardTitle}>Проверка перед отправкой</Typography>
          {checks.map((item) => (
            <div key={item.text} className={classes.checkItem}>
              {item.ok ? (
                <CheckCircleIcon
                  className={classes.checkIcon}
                  style={{ color: colors.green }}
                />
              ) : (
                <CancelIcon
                  className={classes.checkIcon}
                  style={{ color: colors.textFaint }}
                />
              )}
              <span style={{ color: item.ok ? colors.text : colors.textDim }}>
                {item.text}
              </span>
            </div>
          ))}
          <div className={classes.actions} style={{ marginTop: 20 }}>
            <Button
              variant="contained"
              disableElevation
              className={classes.submit}
              startIcon={<SendIcon />}
              disabled={!allOk || submitting}
              onClick={handleSubmit}
            >
              {submitting
                ? 'Отправляем…'
                : editing
                ? 'Отправить изменения на проверку'
                : 'Отправить на проверку в Meta'}
            </Button>
            <Button onClick={onCancel} disabled={submitting}>
              Отмена
            </Button>
          </div>
          {submitError && (
            <Typography variant="body2" className={classes.submitError}>
              {submitError}
            </Typography>
          )}
          {editing ? (
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>
              Изменённый шаблон снова уйдёт на модерацию в Meta. Пока проверка идёт,
              рассылать его нельзя. Meta разрешает менять шаблон не чаще раза в сутки
              и не больше 10 раз за 30 дней.
            </Typography>
          ) : (
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>
              Шаблон уйдёт на модерацию в Meta (обычно от нескольких минут до суток).
              Пока он не одобрен, отправлять рассылки с ним нельзя.
            </Typography>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <div className={classes.sticky}>
          <WhatsappPreview
            text={previewText}
            imageUrl={useImage ? previewImage : ''}
            footer={footer}
            buttons={consentButtons ? CONSENT_BUTTON_TEXTS[language] || [] : []}
            time={currentTime()}
            emptyText="Начните вводить текст шаблона — здесь появится сообщение так, как его увидит клиент."
          />
        </div>
      </Grid>
    </Grid>
  );
}

export default TemplateForm;
